import { z } from "zod";
import { PHOTO_KINDS, ROLES, SHOPS, STATES } from "./enums.js";
import { formConfigSchema } from "./form-config.js";
import { staffPhone, usZip } from "./job.js";

// People, dealerships and job photos: the admin side of the directory.

/**
 * One person, one role. Reps carry their 2-3 dealerships and their manager; a dealership login
 * carries exactly one dealership; managers and admins carry none.
 */
export const personInputSchema = z
  .object({
    name: z.string().trim().min(2).max(80),
    email: z.email().transform((e) => e.toLowerCase()),
    phone: staffPhone.optional(),
    role: z.enum(ROLES),
    storeIds: z.array(z.string().min(1)).max(20).default([]),
    managerId: z.string().min(1).optional(),
    active: z.boolean().default(true),
  })
  .superRefine((p, ctx) => {
    if (p.role === "sales_rep" && p.storeIds.length === 0) {
      ctx.addIssue({ code: "custom", path: ["storeIds"], message: "Pick at least one dealership" });
    }
    if (p.role === "dealership" && p.storeIds.length !== 1) {
      ctx.addIssue({ code: "custom", path: ["storeIds"], message: "A dealership login has exactly one dealership" });
    }
    if (p.role !== "sales_rep" && p.managerId) {
      ctx.addIssue({ code: "custom", path: ["managerId"], message: "Only reps have a manager" });
    }
  });
export type PersonInput = z.input<typeof personInputSchema>;

export const personSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  role: z.enum(ROLES),
  storeIds: z.array(z.string()),
  managerId: z.string().optional(),
  active: z.boolean(),
});
export type Person = z.infer<typeof personSchema>;
export const peopleResponseSchema = z.object({ items: z.array(personSchema) });

export const storeInputSchema = z.object({
  name: z.string().trim().min(2).max(100),
  groupId: z.string().regex(/^grp_[a-z0-9_]+$/, "Use grp_ and lowercase letters, e.g. grp_sun_auto").optional(),
  shop: z.enum(SHOPS),
  state: z.enum(STATES),
  city: z.string().trim().max(60).optional(),
  zip: usZip.optional(),
  timeZone: z.string().default("America/New_York"),
  active: z.boolean().default(true),
  formConfig: formConfigSchema.optional(),
});
export type StoreInput = z.input<typeof storeInputSchema>;

export const adminStoreSchema = z.object({
  storeId: z.string(),
  name: z.string(),
  groupId: z.string().optional(),
  shop: z.enum(SHOPS),
  state: z.enum(STATES).optional(),
  city: z.string().optional(),
  zip: z.string().optional(),
  timeZone: z.string(),
  active: z.boolean(),
  formConfig: formConfigSchema.optional(),
});
export type AdminStore = z.infer<typeof adminStoreSchema>;
export const storesResponseSchema = z.object({ items: z.array(adminStoreSchema) });

export const jobPhotosResponseSchema = z.object({
  items: z.array(z.object({ photoId: z.string(), kind: z.enum(PHOTO_KINDS), url: z.url(), thumbUrl: z.url().optional() })),
});
export type JobPhoto = z.infer<typeof jobPhotosResponseSchema>["items"][number];

/** A sales manager's whole team in one call: these reps report to them, no others. */
export const teamInputSchema = z.object({ repIds: z.array(z.string().min(1)).max(200) });
export type TeamInput = z.input<typeof teamInputSchema>;

// ---- Change log: who changed which person or dealership, when, and from what to what ----

export const AUDIT_ENTITIES = ["person", "store"] as const;
export type AuditEntity = (typeof AUDIT_ENTITIES)[number];

export const auditChangeSchema = z.object({ field: z.string(), before: z.unknown().optional(), after: z.unknown().optional() });
export const auditEntrySchema = z.object({
  auditId: z.string(),
  at: z.iso.datetime(),
  actor: z.object({ userId: z.string(), name: z.string() }),
  entity: z.enum(AUDIT_ENTITIES),
  entityId: z.string(),
  /** Name at the time, so the log reads well after a rename. */
  entityName: z.string(),
  action: z.enum(["created", "updated"]),
  changes: z.array(auditChangeSchema),
});
export type AuditEntry = z.infer<typeof auditEntrySchema>;

/** Newest first. With entityId: one person's or dealership's history (entity required). */
export const auditQuerySchema = z
  .object({
    entity: z.enum(AUDIT_ENTITIES).optional(),
    entityId: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(100),
  })
  .refine((q) => !q.entityId || q.entity, { path: ["entity"], message: "Say whether entityId is a person or a store" });
export type AuditQuery = z.output<typeof auditQuerySchema>;
export const auditResponseSchema = z.object({ items: z.array(auditEntrySchema) });
